import type { Message, MessageListResponse } from "@/lib/core/crm/types";
import { logger } from "@/lib/logger";
import { parseApiError, formatErrorForLogging } from "../error-handler";
import { MessageBaseSchema } from "@/lib/core/crm/schemas";
import type { MessageApiError } from "../error-types";

function validateMessageResponse(data: unknown): Message {
  try {
    return MessageBaseSchema.parse(data);
  } catch (error) {
    const validationError = parseApiError(error);
    if (process.env.NODE_ENV === "development") {
      logger.error("❌ Message validation failed", validationError, {
        formatted: formatErrorForLogging(validationError),
      });
    }
    throw validationError;
  }
}

export async function fetchMessage(
  provider: string,
  messageId: string,
  connectionId: string,
  token?: string
): Promise<Message> {
  const encodedMessageId = encodeURIComponent(messageId);
  const searchParams = new URLSearchParams({
    connectionId,
  });

  const url = `/api/connections/messages/${provider}/${encodedMessageId}?${searchParams}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (error) {
    const apiError = parseApiError(error);
    throw apiError;
  }

  if (!response.ok) {
    throw parseApiError(response);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    throw parseApiError(new Error("Failed to parse response JSON"));
  }

  return validateMessageResponse(data);
}

export async function fetchMessages(
  provider: string,
  params: {
    connectionId: string;
    limit?: number;
    skip?: number;
    filter?: string;
    /**
     * Provider folder/label id to scope the listing to, from `listFolders()`.
     * Omit for the provider's default (mailbox-wide / Inbox).
     */
    folderId?: string;
  },
  token?: string
): Promise<MessageListResponse> {
  const searchParams = new URLSearchParams({
    connectionId: params.connectionId,
    limit: (params.limit || 25).toString(),
    skip: (params.skip || 0).toString(),
  });

  if (params.filter && params.filter !== "all") {
    searchParams.append("filter", params.filter);
  }

  // Only sent when present: an empty `folderId=` would be trimmed away server-side
  // anyway, but omitting it keeps the request URL — and therefore the query key
  // and the server cache key — identical to an unscoped listing.
  if (params.folderId) {
    searchParams.append("folderId", params.folderId);
  }

  const url = `/api/connections/messages/${provider}?${searchParams}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (error) {
    throw parseApiError(error);
  }

  if (!response.ok) {
    throw parseApiError(response);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    throw parseApiError(new Error("Failed to parse response JSON"));
  }

  return data as MessageListResponse;
}
